/**
 * AI-search referral attribution.
 *
 * On first mount of the app, we look at document.referrer and utm_source
 * to see if this visitor arrived from a known AI-assistant host
 * (Perplexity, ChatGPT, Claude, Gemini, Copilot, You, Phind, Poe, Kagi).
 *
 * If so, we:
 *   1. Generate a session id and cache the attribution in localStorage.
 *   2. Fire a `visit` beacon to /api/ai-lead-webhook.
 *
 * From then on, any form-submission code can call `reportAiConversion()`
 * to record the conversion — the util looks up the stored attribution
 * and, if present, fires a `convert` event with the form_type and email.
 *
 * The backend silently drops non-AI events, so callers can invoke this
 * unconditionally without worrying about the referrer.
 */

const STORAGE_KEY = 'ai_attribution_v1';
const ENDPOINT = '/api/ai-lead-webhook';

// Paths where we DON'T inject ai_session_id — this avoids echoing our
// own beacon back on itself, and skips public read-only routes.
const FETCH_INJECT_SKIP = new Set(['/api/ai-lead-webhook']);

// Mirror of the backend's AI host list. Any host in this map is treated
// as AI-referred; the value is the canonical source label sent up.
const AI_HOSTS = {
  'perplexity.ai': 'perplexity',
  'www.perplexity.ai': 'perplexity',
  'chat.openai.com': 'chatgpt',
  'chatgpt.com': 'chatgpt',
  'claude.ai': 'claude',
  'gemini.google.com': 'gemini',
  'bard.google.com': 'gemini',
  'copilot.microsoft.com': 'copilot',
  'you.com': 'you',
  'phind.com': 'phind',
  'poe.com': 'poe',
  'kagi.com': 'kagi',
  'duckduckgo.com': 'ddg-ai',
};

const UTM_AI_SOURCES = new Set([
  'perplexity', 'chatgpt', 'openai', 'claude', 'anthropic',
  'gemini', 'copilot', 'you', 'phind', 'poe', 'kagi',
  'chatbot', 'ai-search', 'llm', 'ai',
]);

function safeParseHost(referrer) {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function detectSource() {
  // 1. explicit utm_source on the landing URL wins
  const params = new URLSearchParams(window.location.search);
  const utm = (params.get('utm_source') || '').toLowerCase().trim();
  if (utm && UTM_AI_SOURCES.has(utm)) {
    return { source: utm, utm_source: utm, utm_medium: params.get('utm_medium') || null, utm_campaign: params.get('utm_campaign') || null };
  }
  // 2. otherwise fall back to document.referrer host
  const host = safeParseHost(document.referrer);
  if (host && AI_HOSTS[host]) {
    return { source: AI_HOSTS[host], utm_source: null, utm_medium: null, utm_campaign: null };
  }
  return null;
}

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(payload) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* localStorage full or blocked — silent */
  }
}

function makeSessionId() {
  // Random 128-bit id, base36-encoded, prefixed for readability.
  const rnd = () => Math.random().toString(36).slice(2, 12);
  return `ai_${Date.now().toString(36)}_${rnd()}${rnd()}`;
}

async function beacon(payload) {
  // Fire-and-forget. The backend rate-limits + validates; we don't want a
  // failed beacon to leak into user-facing errors.
  try {
    // navigator.sendBeacon would be ideal but doesn't set Content-Type to
    // application/json portably — a fetch keepalive is more reliable.
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    console.debug('[ai-attribution] beacon failed', err);
  }
}

/**
 * Detect and record a `visit` event if this looks like an AI referral.
 * Safe to call multiple times — only fires once per browser (per storage).
 */
export function initAiAttribution() {
  if (typeof window === 'undefined') return;

  // Install the fetch injector unconditionally — it no-ops when there's
  // no stored session, so it costs nothing on non-AI traffic.
  installFetchAttribution();

  // Already tagged? Nothing else to do.
  if (readStored()) return;

  const detected = detectSource();
  if (!detected) return;

  const record = {
    session_id: makeSessionId(),
    source: detected.source,
    referrer: document.referrer || '',
    landing_page: window.location.pathname + window.location.search,
    utm_source: detected.utm_source,
    utm_medium: detected.utm_medium,
    utm_campaign: detected.utm_campaign,
    first_seen_at: new Date().toISOString(),
  };
  writeStored(record);

  beacon({
    session_id: record.session_id,
    event_type: 'visit',
    referrer: record.referrer,
    landing_page: record.landing_page,
    utm_source: record.utm_source,
    utm_medium: record.utm_medium,
    utm_campaign: record.utm_campaign,
    client_source: record.source,
  });
}

/**
 * Return the AI-beacon session_id for this browser, or ``null`` if the
 * visitor wasn't tagged as an AI referral. Forms attach this to their
 * payload so the backend can join the submission to its earlier `visit`
 * event and record a real `convert` event.
 */
export function getAiSessionId() {
  if (typeof window === 'undefined') return null;
  const stored = readStored();
  return stored?.session_id || null;
}

/**
 * Monkey-patch ``window.fetch`` once so every POST/PUT/PATCH to an
 * ``/api/*`` route with a JSON body silently gets an ``ai_session_id``
 * field appended (when the browser has been tagged as AI-referred).
 *
 * Two safeguards:
 *   • Multipart bodies (``FormData``) are left alone — modal callers
 *     that use FormData (FastQuote, ConsultationIntake) append the field
 *     themselves.
 *   • We only touch requests whose body parses as a JSON *object*. Arrays,
 *     strings, or non-JSON bodies pass through untouched.
 *
 * Skipping this route: ``/api/ai-lead-webhook`` (would echo the beacon
 * back on itself).
 */
function installFetchAttribution() {
  if (typeof window === 'undefined' || !window.fetch) return;
  if (window.__aiFetchPatched) return;
  window.__aiFetchPatched = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init) {
    try {
      const sid = getAiSessionId();
      if (!sid || !init || !init.body) return originalFetch(input, init);
      const method = (init.method || 'GET').toUpperCase();
      if (!['POST', 'PUT', 'PATCH'].includes(method)) return originalFetch(input, init);

      // URL check — support string, URL, and Request inputs
      let url = '';
      if (typeof input === 'string') url = input;
      else if (input instanceof URL) url = input.pathname;
      else if (input && typeof input.url === 'string') url = input.url;
      if (!url.includes('/api/')) return originalFetch(input, init);

      // Extract just the pathname for skip-list comparison
      let pathname = url;
      try {
        pathname = new URL(url, window.location.origin).pathname;
      } catch { /* relative — use as-is */ }
      if (FETCH_INJECT_SKIP.has(pathname)) return originalFetch(input, init);

      // Only touch JSON *object* bodies
      const body = init.body;
      if (body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob) {
        return originalFetch(input, init);
      }
      if (typeof body !== 'string') return originalFetch(input, init);

      let parsed;
      try { parsed = JSON.parse(body); } catch { return originalFetch(input, init); }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return originalFetch(input, init);
      }
      if (parsed.ai_session_id) return originalFetch(input, init);

      const nextInit = { ...init, body: JSON.stringify({ ...parsed, ai_session_id: sid }) };
      return originalFetch(input, nextInit);
    } catch (err) {
      // Never break the caller's fetch on an attribution bug.
      console.debug('[ai-attribution] fetch patch error', err);
      return originalFetch(input, init);
    }
  };
}

/**
 * Report a successful form submission as an AI-attributed conversion.
 * No-op when the visitor was not tagged as an AI referral.
 *
 * @param {{ form_type: string, contact_email?: string }} args
 */
export function reportAiConversion({ form_type, contact_email }) {
  if (typeof window === 'undefined') return;
  const stored = readStored();
  if (!stored) return;

  beacon({
    session_id: stored.session_id,
    event_type: 'convert',
    referrer: stored.referrer || '',
    landing_page: stored.landing_page || '',
    utm_source: stored.utm_source || null,
    utm_medium: stored.utm_medium || null,
    utm_campaign: stored.utm_campaign || null,
    form_type: form_type || 'unknown',
    contact_email: (contact_email || '').trim() || null,
    client_source: stored.source,
  });
}
